// @systeme: interface
// Une préparation : la date-pivot, ses étapes (des éléments ordinaires : fait, reporter,
// corriger, supprimer), la proposition de décaler si la date a bougé, une étape à ajouter,
// et la question du bon moment sur ce qui est fait.
import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ChevronLeft } from 'lucide-react';
import { Link as LienRouteur, useNavigate, useParams } from 'react-router';
import type { Retroplanning } from '@foyer/shared';
import { useActionsElements } from '../composants/ActionsElements';
import { Chargement } from '../composants/Chargement';
import { LIBELLES_AVIS } from '../composants/DialoguesPreparation';
import { useMessages } from '../composants/Messages';
import { api } from '../lib/api';
import { formaterJourCourt, formaterJourLong, jourDe } from '../lib/dates';
import { couleurs, tailles } from '../theme/theme';
import { premiereMajuscule } from './Aujourdhui';

export function Preparation() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { dire, direErreur } = useMessages();
  const [preparation, setPreparation] = useState<Retroplanning | undefined>();
  const [aujourdhui, setAujourdhui] = useState<{ jour: string; jourDemain: string } | undefined>();
  const [ajout, setAjout] = useState(false);
  const [titre, setTitre] = useState('');
  const [joursAvant, setJoursAvant] = useState('7');
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(async () => {
    try {
      setPreparation(await api.retroplannings.lire(id));
    } catch (e) {
      direErreur(e);
    }
  }, [id, direErreur]);

  useEffect(() => {
    api.retroplannings.lire(id).then(setPreparation).catch(direErreur);
    api.temps.aujourdhui().then(setAujourdhui).catch(direErreur);
  }, [id, direErreur]);

  const { ligneAvec, feuilles } = useActionsElements({
    jourAujourdhui: aujourdhui?.jour ?? '',
    jourDemain: aujourdhui?.jourDemain ?? '',
    recharger: charger,
    ecran: '/a-venir/preparations',
  });

  async function agir(action: () => Promise<unknown>, confirmation: string) {
    setEnCours(true);
    try {
      await action();
      dire(confirmation);
      await charger();
    } catch (e) {
      direErreur(e);
    } finally {
      setEnCours(false);
    }
  }

  if (!preparation) return <Chargement />;
  const p = preparation;
  const restantes = p.jalons.filter((j) => j.element.statut !== 'fait');

  return (
    <Box sx={{ px: `${tailles.marge}px`, pt: 3, pb: 4 }}>
      <Button
        component={LienRouteur}
        to="/a-venir/preparations"
        startIcon={<ChevronLeft size={18} />}
        color="inherit"
        sx={{ px: 0, mb: 1 }}
      >
        Ce que je prépare
      </Button>
      <Typography variant="h1" component="h1">
        {p.pivot.titre}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {p.pivot.debutLe
          ? premiereMajuscule(formaterJourLong(jourDe(p.pivot.debutLe)))
          : 'Sans date pour le moment'}
        {p.modeleNom ? ` · ${p.modeleNom}` : ''}
      </Typography>

      {p.aDecaler && (
        <Paper
          variant="outlined"
          sx={{ p: 1.5, mb: 2, bgcolor: couleurs.terreCuiteClair, borderColor: 'transparent' }}
        >
          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            La date a bougé. Je décale les {restantes.length} étapes qui restent ?
          </Typography>
          <Button
            variant="contained"
            disabled={enCours}
            onClick={() =>
              void agir(() => api.retroplannings.propager(p.id), 'Les étapes sont décalées.')
            }
          >
            Décaler
          </Button>
        </Paper>
      )}

      {p.jalons.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          Pas encore d’étape. Ajoute ce qu’il faut faire avant, et combien de jours avant.
        </Typography>
      ) : (
        <List disablePadding aria-label="Étapes">
          {p.jalons.map((j) => (
            <Box key={j.id}>
              {ligneAvec(
                j.element,
                j.element.debutLe
                  ? `${formaterJourCourt(jourDe(j.element.debutLe))}${j.deplaceALaMain ? ' · déplacée par toi' : ''}`
                  : 'sans date',
              )}
              {j.element.statut === 'fait' && j.avis === null && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, pl: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    C’était le bon moment ?
                  </Typography>
                  {LIBELLES_AVIS.map((a) => (
                    <Button
                      key={a.valeur}
                      size="small"
                      variant="outlined"
                      disabled={enCours}
                      onClick={() =>
                        void agir(
                          () => api.retroplannings.donnerAvis(j.id, a.valeur),
                          'Merci, c’est noté.',
                        )
                      }
                    >
                      {a.libelle}
                    </Button>
                  ))}
                </Stack>
              )}
            </Box>
          ))}
        </List>
      )}

      {ajout ? (
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            void agir(
              () =>
                api.retroplannings.ajouterJalon(p.id, {
                  titre: titre.trim(),
                  decalageJours: -Math.abs(Number.parseInt(joursAvant, 10) || 0),
                }),
              'C’est ajouté.',
            ).then(() => {
              setAjout(false);
              setTitre('');
            });
          }}
          sx={{ mt: 2 }}
        >
          <Stack spacing={1.5}>
            <TextField
              label="Quoi ?"
              value={titre}
              onChange={(e) => setTitre(e.target.value)}
              autoFocus
              inputProps={{ maxLength: 200 }}
            />
            <TextField
              type="number"
              label="Combien de jours avant ?"
              value={joursAvant}
              onChange={(e) => setJoursAvant(e.target.value)}
              inputProps={{ min: 0, max: 365 }}
              sx={{ maxWidth: 220 }}
            />
            <Stack direction="row" spacing={1}>
              <Button type="submit" variant="contained" disabled={enCours || titre.trim() === ''}>
                Ajouter
              </Button>
              <Button color="inherit" onClick={() => setAjout(false)}>
                Annuler
              </Button>
            </Stack>
          </Stack>
        </Box>
      ) : (
        <Button onClick={() => setAjout(true)} sx={{ mt: 1 }}>
          Ajouter une étape
        </Button>
      )}

      <Box sx={{ mt: 4 }}>
        <Button
          color="inherit"
          disabled={enCours}
          onClick={() =>
            void agir(
              () => api.retroplannings.supprimer(p.id),
              'D’accord, je ne prépare plus.',
            ).then(() => void navigate('/a-venir/preparations'))
          }
        >
          Ne plus préparer
        </Button>
      </Box>

      {feuilles}
    </Box>
  );
}
